// GalleryBlockEditor — multi-image gallery with layout switcher.
//
// Supports adding files via picker or drag-drop; reordering by clicking
// move arrows; removing by × on each thumbnail.

import { useState } from "react";
import type { GalleryBlock, Block } from "../../../lib/blocks";
import { fileUrl } from "../../../lib/pb";
import { MediaPicker } from "../MediaPicker";
import { t } from "../theme";

export interface GalleryBlockEditorProps {
  block: GalleryBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
  articleId: string;
  mediaAssets: string[];
  onUploadFile: (file: File) => Promise<string>;
}

const LAYOUTS: { value: GalleryBlock["layout"]; label: string }[] = [
  { value: "grid-2", label: "2 cols" },
  { value: "grid-3", label: "3 cols" },
  { value: "grid-4", label: "4 cols" },
  { value: "masonry", label: "Masonry" },
];

export function GalleryBlockEditor({
  block,
  onChange,
  isSelected,
  articleId,
  mediaAssets,
  onUploadFile,
}: GalleryBlockEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const filenames: string[] = block.files || [];

  function appendFile(filename: string) {
    onChange({ files: [...filenames, filename] } as Partial<GalleryBlock>);
  }

  async function handleDropFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setErr(null);
    const next: string[] = [...filenames];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith("image/")) continue;
      try {
        const filename = await onUploadFile(f);
        next.push(filename);
      } catch (e) {
        setErr((e as Error).message);
      }
    }
    onChange({ files: next } as Partial<GalleryBlock>);
  }

  function removeAt(idx: number) {
    const next = filenames.filter((_, i) => i !== idx);
    onChange({ files: next } as Partial<GalleryBlock>);
  }

  function moveItem(idx: number, dir: -1 | 1) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= filenames.length) return;
    const next = [...filenames];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange({ files: next } as Partial<GalleryBlock>);
  }

  const cols =
    block.layout === "grid-2" ? 2 :
    block.layout === "grid-3" ? 3 :
    block.layout === "grid-4" ? 4 :
    3; // masonry approximated with 3 columns visually

  return (
    <div>
      {isSelected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans }}>
          {LAYOUTS.map((l) => (
            <button
              key={l.value}
              type="button"
              onClick={() => onChange({ layout: l.value } as Partial<GalleryBlock>)}
              style={pillStyle(block.layout === l.value)}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          void handleDropFiles(e.dataTransfer.files);
        }}
        style={{
          padding: filenames.length === 0 ? "32px 16px" : 8,
          borderRadius: 8,
          border: `2px dashed ${drag ? t.accent : filenames.length ? "transparent" : t.border}`,
          background: drag ? t.accentSoft : "transparent",
          transition: t.transition,
        }}
      >
        {filenames.length === 0 ? (
          <div
            onClick={() => setPickerOpen(true)}
            style={{
              textAlign: "center",
              color: t.text3,
              fontFamily: t.sans,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 20, color: t.text4, marginBottom: 6 }}>▦</div>
            Drop images here or click to add
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: 6,
            }}
          >
            {filenames.map((f, idx) => (
              <div
                key={`${f}-${idx}`}
                style={{
                  position: "relative",
                  borderRadius: 4,
                  overflow: "hidden",
                  background: t.cardRaised,
                }}
              >
                <img
                  src={fileUrl("articles", articleId, f, "480x320")}
                  alt=""
                  style={{
                    width: "100%",
                    aspectRatio: "3 / 2",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      display: "flex",
                      gap: 2,
                      background: "rgba(255,255,255,0.92)",
                      borderRadius: 4,
                      padding: 2,
                    }}
                  >
                    <IconBtn onClick={() => moveItem(idx, -1)} title="Move left">←</IconBtn>
                    <IconBtn onClick={() => moveItem(idx, 1)} title="Move right">→</IconBtn>
                    <IconBtn onClick={() => removeAt(idx)} title="Remove" danger>×</IconBtn>
                  </div>
                )}
              </div>
            ))}
            {isSelected && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                style={{
                  aspectRatio: "3 / 2",
                  border: `2px dashed ${t.border}`,
                  borderRadius: 4,
                  background: "transparent",
                  color: t.text3,
                  fontSize: 18,
                  cursor: "pointer",
                  fontFamily: t.sans,
                }}
              >
                +
              </button>
            )}
          </div>
        )}
      </div>

      {err && (
        <div style={{ marginTop: 6, fontSize: 11, color: t.danger, fontFamily: t.sans }}>
          {err}
        </div>
      )}

      {isSelected && (
        <input
          type="text"
          value={block.caption || ""}
          onChange={(e) => onChange({ caption: e.target.value } as Partial<GalleryBlock>)}
          placeholder="Gallery caption (optional)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 8,
            padding: "6px 8px",
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 4,
            fontSize: 12,
            color: t.text,
            outline: "none",
            fontFamily: t.sans,
          }}
        />
      )}

      {pickerOpen && (
        <MediaPicker
          articleId={articleId}
          mediaAssets={mediaAssets}
          accept="image"
          onPick={(filename) => {
            appendFile(filename);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
          onUploadFile={onUploadFile}
        />
      )}
    </div>
  );
}

function IconBtn({
  onClick,
  title,
  danger,
  children,
}: {
  onClick: () => void;
  title: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        width: 22,
        height: 22,
        padding: 0,
        border: "none",
        background: "transparent",
        color: danger ? t.danger : t.text2,
        fontSize: 12,
        lineHeight: 1,
        borderRadius: 3,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
  };
}
